import React, { useState } from "react";
import { Mail, Clock, MessageSquare, Send } from "lucide-react";
import { useCountry } from "../contexts/CountryContext";

const ContactPage = () => {
  const { country } = useCountry();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <div className="page" data-testid="contact-page" style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div className="page-header" style={{ textAlign: 'center', marginBottom: '36px' }}>
        <h1>Contact Us</h1>
        <p>Have a question, feedback, or need help? We're here for you.</p>
      </div>

      {/* Info cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '32px' }}>
        {[
          { icon: Mail, title: 'Email Support', desc: country.supportEmail, sub: 'For general enquiries', testId: 'contact-support-email' },
          { icon: Clock, title: 'Response Time', desc: 'Within 24 hours', sub: 'Monday to Friday' },
          { icon: MessageSquare, title: 'Help Centre', desc: 'Browse our FAQ', sub: 'Quick answers to common questions' },
        ].map(item => (
          <div key={item.title} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '22px', textAlign: 'center' }}>
            <item.icon size={22} color="var(--accent)" style={{ marginBottom: '10px' }} />
            <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-dark)', marginBottom: '4px' }}>{item.title}</h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-dark)', margin: '0 0 2px', fontWeight: 500 }} data-testid={item.testId}>{item.desc}</p>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>{item.sub}</p>
          </div>
        ))}
      </div>

      {/* Contact form */}
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '32px' }}>
        {submitted ? (
          <div data-testid="contact-success" style={{ textAlign: 'center', padding: '32px 0' }}>
            <Send size={36} color="#047857" style={{ marginBottom: '16px' }} />
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>Message Sent</h2>
            <p style={{ fontSize: '0.9375rem', color: 'var(--text-muted)', marginBottom: '20px' }}>Thank you for reaching out. We'll get back to you within 24 hours.</p>
            <button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }} className="btn-outline" data-testid="send-another">Send Another Message</button>
          </div>
        ) : (
          <>
            <h2 style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--text-dark)', marginBottom: '20px' }}>Send us a message</h2>
            <form onSubmit={handleSubmit} data-testid="contact-form" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-dark)', marginBottom: '6px' }}>Name</label>
                  <input type="text" value={form.name} onChange={set('name')} required placeholder="Your name"
                    data-testid="contact-name"
                    style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '0.875rem' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-dark)', marginBottom: '6px' }}>Email</label>
                  <input type="email" value={form.email} onChange={set('email')} required placeholder="your@email.com"
                    data-testid="contact-email"
                    style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '0.875rem' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-dark)', marginBottom: '6px' }}>Subject</label>
                <input type="text" value={form.subject} onChange={set('subject')} required placeholder="What's this about?"
                  data-testid="contact-subject"
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '0.875rem' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-dark)', marginBottom: '6px' }}>Message</label>
                <textarea value={form.message} onChange={set('message')} required rows={5} placeholder="Tell us how we can help..."
                  data-testid="contact-message"
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '0.875rem', resize: 'vertical' }} />
              </div>
              <button type="submit" className="btn-primary" data-testid="contact-submit" style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Send size={16} /> Send Message
              </button>
            </form>
          </>
        )}
      </div>

      <p style={{ textAlign: 'center', fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: '20px' }}>
        For urgent account issues, please include your registered email address in the message.
      </p>
    </div>
  );
};

export default ContactPage;
